import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import { getAuth } from "firebase/auth";
import type { AppRouter } from "@graftus/api-business-functions/trpc-router";

function getBaseUrl(): string {
  const url = import.meta.env.VITE_BUSINESS_API_URL;
  if (!url) {
    if (import.meta.env.MODE === "test" || typeof process !== "undefined" && process.env.VITEST) {
      return "http://localhost:5001";
    }
    throw new Error("VITE_BUSINESS_API_URL is not set");
  }
  const baseUrl = url.replace(/\/$/, "");
  if (import.meta.env.DEV && import.meta.env.MODE !== "test") {
    const target = new URL(baseUrl);
    const isLocalDevelopmentApi =
      ["localhost", "127.0.0.1", "[::1]"].includes(target.hostname) &&
      target.pathname === "/graftus-development/europe-west1/apiBusiness";
    const isHostedDevelopmentApi =
      target.protocol === "https:" &&
      target.hostname === "europe-west1-graftus-development.cloudfunctions.net" &&
      target.pathname === "/apiBusiness";
    if (!isLocalDevelopmentApi && !isHostedDevelopmentApi) {
      throw new Error(
        "Local development requires VITE_BUSINESS_API_URL to target graftus-development.",
      );
    }
  }
  return baseUrl;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${getBaseUrl()}/trpc`,
      transformer: superjson,
      async headers() {
        return getAuthHeaders();
      },
    }),
  ],
});
